import React, { useState, useEffect } from "react";
import { ScrollToTop } from "../components/ScrollToTop";
import { toast, ToastContainer } from 'react-toastify';
import { ADMIN_TOAST_CONTAINER_ID } from "../helper/ShowToast";
import * as actions from '../redux/actions/index';
import { fetchAccessContext } from '../redux/actions/auth';
import { useDispatch, useSelector } from "react-redux";
import { shouldFetchArtistInfo } from "../helper/access";
import {
    BILLING_COMPLETE_EVENT,
    restoreTabRendering,
    subscribeToBillingComplete,
    tryRedirectBillingReturnToOpener,
} from "../helper/billingReturn";

import { AdminHeader } from "../components/admin/AdminHeader";
import { AdminSidebar } from "../components/admin/AdminSidebar";
import { useNavigation } from "../context/NavigationContext";
import { generateMetadata } from "../seo/metadataGenerator";
import { DEFAULT_SEO } from "../seo/constants";
import SeoHead from "../seo/SeoHead";

export const AdminLayout = ({ children }) => {
    const dispatch = useDispatch();
    const { pageKey } = useNavigation();
    const access = useSelector((state) => state.auth.access);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const { hasActiveSubscription } = useSelector((state) => state.billing);
    const legacyArtistId = userDetails?.assignedArtistSongstateId;
    const activeArtistProfileId = access?.artist?.activeArtistProfileId || null;
    const canFetchArtistInfo = shouldFetchArtistInfo(access, userDetails, {
        hasActiveSubscription,
    });

    // Stripe return tab: send user back to the original tab and close this popup.
    useEffect(() => {
        tryRedirectBillingReturnToOpener();
    }, []);

    // Original tab: refresh billing when Stripe popup completes.
    useEffect(() => {
        return subscribeToBillingComplete(
            dispatch,
            actions.refreshWorkspaceSession,
            actions.getCreditDetails
        );
    }, [dispatch]);

    // Fetch access on admin entry and when linked artist changes — not when access updates (avoids loop).
    useEffect(() => {
        dispatch(fetchAccessContext());
    }, [dispatch, legacyArtistId]);

    // Marketing pages hide the native cursor. Private HTML can inherit that from
    // prerendered index.html, so restore it whenever the admin shell mounts.
    useEffect(() => {
        document.body.style.removeProperty("cursor");
    }, []);

    useEffect(() => {
        if (!canFetchArtistInfo) return;
        dispatch(actions.getArtistInfo(toast));
    }, [dispatch, canFetchArtistInfo, legacyArtistId, activeArtistProfileId]);

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [shellKey, setShellKey] = useState(0);

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Re-mount shell chrome after Stripe return — fixes stale images in background tabs.
    useEffect(() => {
        const refreshShell = () => {
            restoreTabRendering();
            window.setTimeout(() => setShellKey((key) => key + 1), 400);
        };
        window.addEventListener(BILLING_COMPLETE_EVENT, refreshShell);
        return () => window.removeEventListener(BILLING_COMPLETE_EVENT, refreshShell);
    }, []);

    return (  
        <>
            <SeoHead
                metadata={generateMetadata({ ...DEFAULT_SEO, path: "/" })}
                noIndex
            />
            <ToastContainer
                containerId={ADMIN_TOAST_CONTAINER_ID}
                enableMultiContainer
                position="top-center"
                theme="colored"
                hideProgressBar
                style={{ zIndex: 200000 }}
            />

            <ScrollToTop />

            <AdminSidebar
                key={`admin-sidebar-${shellKey}`}
                isCollapsed={isCollapsed}
                toggleSidebar={toggleSidebar}
            />

            <div
                key={`${pageKey}-${shellKey}`}
                className={`admin-main-content ${isCollapsed ? 'lg:max-w-[calc(100%-70px)]' : 'lg:max-w-[calc(100%-200px)]'} ml-auto`}
            >
                <AdminHeader />
                {children}
            </div>
        </>
    )
}
